import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { QUERY_KEYS } from "@/machines/api/config.ts";
import getEvents, {
  batchEditTruckEvents,
  getEventById,
  getTruckEvents,
  getTruckEventsOverview,
  IGetEventsInput,
  IGetTruckEventsOverviewInput,
  TruckEventBatchEditPayload,
  TruckEventBatchEditResult,
} from "@/machines/api/requests/events.requests.ts";
import { ApiResponse } from "@/shared/types/api.ts";
import { MachineEvent, TruckEvent, TruckEventOverview } from "@/machines/types/event.ts";

/** Cache lifetime for rolling-window query keys (realtime mode mints a new key every 5 minutes). */
export const ROLLING_WINDOW_GC_MS = 15 * 60 * 1000;

/** Query options for a rolling (realtime) window — short cache, no loading flash on window shifts. */
export const ROLLING_WINDOW_OPTIONS = { gcTime: ROLLING_WINDOW_GC_MS, keepPrevious: true } as const;

/**
 * Ceiling on how much of one window a timeline will pull into the browser.
 *
 * Measured on CSN / Casa de Pedra (device 3808844274): a single machine
 * produces ~10 300 raw events per DAY. So an uncapped month is ~300 000
 * segments — more than the timeline can hold, let alone draw. 50 000 is about
 * five days of one machine: enough that the ranges operators actually review
 * come back whole, and bounded enough that a careless 90-day range degrades
 * into "incomplete, and says so" rather than into a dead tab.
 *
 * Reaching it sets `complete: false` on the hook result. NOTHING SURFACES THAT
 * YET — no component reads `complete`, `totalItems` or `loadedItems` — so today
 * a window that hits the cap still renders like a whole one. Measured: one
 * machine produced 57 456 events in 7 days, so the cap is reachable in ordinary
 * use, not just by a careless 90-day range.
 */
export const MAX_WINDOW_EVENTS = 50_000;

/**
 * Which page to ask for next, or `undefined` to stop.
 *
 * Extracted so the stopping rules can be tested without a backend — an
 * off-by-one here is either a hole in the timeline or a request loop.
 */
export const nextEventPage = <T>(
  lastPage: ApiResponse<T[]> | undefined,
  allPages: (ApiResponse<T[]> | undefined)[],
  fetchAll: boolean,
): number | undefined => {
  if (!fetchAll) return undefined;
  // An empty page ends the walk whatever the count claims. Without this a
  // `totalItems` that disagrees with what the endpoint actually returns — a
  // filtered listing, a row deleted mid-walk — asks forever.
  if (!lastPage?.data?.length) return undefined;
  const total = lastPage.metadata?.totalItems;
  if (total == null) return undefined;
  const loaded = allPages.reduce((n, page) => n + (page?.data?.length ?? 0), 0);
  if (loaded >= total || loaded >= MAX_WINDOW_EVENTS) return undefined;
  return allPages.length + 1;
};

/** Result of a paginated event query, shaped like the single-page one it replaced. */
export interface PaginatedEventsResult<T> {
  data: ApiResponse<T[]> | undefined;
  status: "pending" | "error" | "success";
  dataUpdatedAt: number;
  isFetching: boolean;
  /** Every page the window holds has landed. False while still walking, and */
  /** false for good once MAX_WINDOW_EVENTS cut the walk short. */
  complete: boolean;
  /** What the server says the window holds, against what we have. */
  totalItems: number | undefined;
  loadedItems: number;
}

export interface IEventQueryOptions {
  /** Poll interval in ms (e.g. live timeline auto-refresh); omit for no polling. */
  refetchInterval?: number | false;
  /** Cache lifetime override — use a short one for rolling-window keys. */
  gcTime?: number;
  /** Show the previous window's data while a new query key is fetching. */
  keepPrevious?: boolean;
  /**
   * Walk every page of the window instead of taking the first one.
   *
   * Opt-in, because "one page" is the right answer for a paged TABLE — it asks
   * for ten rows and renders ten rows — and the wrong answer for a TIMELINE,
   * which draws a time range and silently loses whatever did not fit. Defaults
   * off so a caller that never asked keeps making exactly one request.
   */
  fetchAll?: boolean;
}

/**
 * One window's events, walked page by page instead of taken from page 1 alone.
 *
 * Both event endpoints are paginated and BOTH were being asked for page 1 and
 * nothing else, with the response `metadata` — which carries `totalItems` and
 * an explicit `truncated` flag — thrown away. Past the page size the timeline
 * simply stopped, and said nothing: a partial window rendered exactly like a
 * complete one. At ~10 300 events per machine-day that binds after about two
 * days on `/api/events/` (page size 20 000) and about a month on
 * `/api/truck-events/` (page size 1 000).
 *
 * Pages land one at a time and each one widens `data`, so the timeline fills in
 * progressively rather than blocking on the whole range. The caller's
 * `itemsPerPage` stays the PAGE size — realtime windows still resolve in a
 * single request, so the live path costs exactly what it did before.
 */
const usePaginatedEvents = <T>(
  queryKey: string,
  fetchPage: (params: IGetEventsInput, signal?: AbortSignal) => Promise<ApiResponse<T[]>>,
  params: IGetEventsInput,
  options?: IEventQueryOptions,
): PaginatedEventsResult<T> => {
  const query = useInfiniteQuery({
    queryKey: [queryKey, params],
    // The caller's page, not 1: a paged table asks for page 3 and must get
    // page 3. Only the fetchAll walk starts somewhere and keeps going.
    initialPageParam: params.page ?? 1,
    queryFn: ({ pageParam, signal }) => fetchPage({ ...params, page: pageParam }, signal),
    getNextPageParam: (lastPage, allPages) =>
      nextEventPage(lastPage, allPages, options?.fetchAll ?? false),
    enabled: !!params && !!params.device_mxid && !!params.itemsPerPage,
    refetchInterval: options?.refetchInterval ?? false,
    ...(options?.gcTime !== undefined && { gcTime: options.gcTime }),
    ...(options?.keepPrevious && { placeholderData: keepPreviousData }),
  });

  // Walk the rest in the background. One page in flight at a time on purpose:
  // the point is to keep extending what is already on screen, not to open a
  // dozen sockets against a window the operator may scroll away from.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    // `cancelRefetch: false`: with the default (true) this call ABORTS any
    // in-flight fetch on the key — a resumed walk on remount would cancel the
    // staleness re-walk and keep serving day-old early pages, and co-observers
    // of a shared key would abort each other's page fetch on every advance.
    // A swallowed call is not lost: it returns the in-flight promise, and this
    // effect re-fires when that fetch settles (`fetchNextPage` gets a new
    // identity with every new observer result).
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage({ cancelRefetch: false });
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const pages = query.data?.pages;
  const data = useMemo(() => {
    if (!pages?.length) return undefined;
    const last = pages[pages.length - 1];
    return { ...last, data: pages.flatMap((page) => page?.data ?? []) } as ApiResponse<T[]>;
  }, [pages]);

  const loadedItems = data?.data.length ?? 0;
  const totalItems = pages?.[pages.length - 1]?.metadata?.totalItems;

  return {
    data,
    status: query.status,
    dataUpdatedAt: query.dataUpdatedAt,
    isFetching: query.isFetching,
    complete: !hasNextPage && query.status === "success",
    totalItems,
    loadedItems,
  };
};

const useGetEvents = (params: IGetEventsInput, options?: IEventQueryOptions) =>
  usePaginatedEvents<MachineEvent>(QUERY_KEYS.EVENTS, getEvents, params, options);

export const useGetEventById = (id: string) => {
  return useQuery<ApiResponse<MachineEvent>>({
    queryKey: [QUERY_KEYS.EVENTS, id],
    queryFn: ({ signal }) => getEventById(id, signal),
    enabled: !!id
  })
}

const useGetTruckEvents = (params: IGetEventsInput, options?: IEventQueryOptions) =>
  usePaginatedEvents<TruckEvent>(QUERY_KEYS.TRUCK_EVENTS, getTruckEvents, params, options);

const useGetTruckEventsOverview = (params: IGetTruckEventsOverviewInput) => {
  return useQuery<ApiResponse<TruckEventOverview>>({
    queryKey: [QUERY_KEYS.TRUCK_EVENTS_OVERVIEW, params],
    queryFn: ({ signal }) => getTruckEventsOverview(params, signal),
    enabled: !!params && !!params.device_mxid
  })
}

const useBatchEditTruckEvents = () => {
  const queryClient = useQueryClient();

  return useMutation<ApiResponse<TruckEventBatchEditResult>, Error, TruckEventBatchEditPayload>({
    mutationFn: batchEditTruckEvents,
    onSuccess: async () => {
      // Await invalidation so fresh data is in cache before the caller clears local edits
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRUCK_EVENTS] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRUCK_EVENTS_OVERVIEW] }),
      ]);
    },
  });
}

export {
  useGetEvents,
  useGetTruckEvents,
  useGetTruckEventsOverview,
  useBatchEditTruckEvents
}